import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PhotoRecordCreateInput,
  PhotoRecordDto,
  PhotoRecordListQuery,
  PhotoRecordListResponse,
  NearbyParcelsResponse,
} from '@forest/contracts';
import { api } from '@/lib/api-client';

type ListParams = Partial<PhotoRecordListQuery>;

function toSearchParams(q: ListParams): URLSearchParams {
  const p = new URLSearchParams();
  if (q.page !== undefined) p.set('page', String(q.page));
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.search) p.set('search', q.search);
  if (q.raion) p.set('raion', q.raion);
  // CSV multi-select params: pass empty string through so the server's
  // parseStringsParam/parseYearsParam picks up the null sentinel.
  if (q.raioane !== undefined) p.set('raioane', q.raioane);
  if (q.anul !== undefined) p.set('anul', String(q.anul));
  if (q.year !== undefined) p.set('year', String(q.year));
  if (q.years !== undefined) p.set('years', q.years);
  if (q.anulMin !== undefined) p.set('anulMin', String(q.anulMin));
  if (q.anulMax !== undefined) p.set('anulMax', String(q.anulMax));
  if (q.semester !== undefined) p.set('semester', String(q.semester));
  if (q.parcelId) p.set('parcelId', q.parcelId);
  if (q.hasParcel !== undefined) p.set('hasParcel', String(q.hasParcel));
  if (q.bbox) p.set('bbox', q.bbox);
  if (q.polygonStatus) p.set('polygonStatus', q.polygonStatus);
  if (q.polygonStatuses !== undefined) p.set('polygonStatuses', q.polygonStatuses);
  if (q.project) p.set('project', q.project);
  if (q.month !== undefined) p.set('month', String(q.month));
  if (q.authorId) p.set('authorId', q.authorId);
  if (q.authorCods !== undefined) p.set('authorCods', q.authorCods);
  if (q.entitatiSilvice !== undefined) p.set('entitatiSilvice', q.entitatiSilvice);
  // Location / technical filters mirroring the parcel route — used by
  // /app/filter so the photo layer narrows alongside polygons.
  if (q.primarii !== undefined) p.set('primarii', q.primarii);
  if (q.ocoaleSilvice !== undefined) p.set('ocoaleSilvice', q.ocoaleSilvice);
  if (q.codProiect) p.set('codProiect', q.codProiect);
  if (q.codProiecte !== undefined) p.set('codProiecte', q.codProiecte);
  if (q.ucf) p.set('ucf', q.ucf);
  if (q.ucfs !== undefined) p.set('ucfs', q.ucfs);
  if (q.projectId) p.set('projectId', q.projectId);
  if (q.suprafataMin !== undefined) p.set('suprafataMin', String(q.suprafataMin));
  if (q.suprafataMax !== undefined) p.set('suprafataMax', String(q.suprafataMax));
  if (q.includeDeleted) p.set('includeDeleted', 'true');
  if (q.sort) p.set('sort', q.sort);
  if (q.order) p.set('order', q.order);
  return p;
}

export function usePhotos(params: ListParams, opts?: { enabled?: boolean }) {
  return useQuery<PhotoRecordListResponse>({
    queryKey: ['photos', params],
    queryFn: async () =>
      api
        .get('photos', { searchParams: toSearchParams(params) })
        .json<PhotoRecordListResponse>(),
    placeholderData: (prev) => prev,
    enabled: opts?.enabled ?? true,
  });
}

type PhotoFacets = {
  raioane: string[];
  hasNullRaion?: boolean;
  ani: number[];
  hasNullYear?: boolean;
  parcelIds: string[];
  authors?: Array<{
    id: string;
    fullName: string;
    cod: string | null;
    count: number;
  }>;
  entitatiSilvice?: string[];
  hasNullEntitateSilvica?: boolean;
  /** Union of the photos' own values and their linked polygons' — a
   *  linked photo's own copies are nulled server-side. */
  codProiecte?: string[];
  ucfs?: string[];
};

/**
 * A single photo by id, for the `?photo=<id>` deep link into Baza de
 * date. Kept separate from the list query on purpose: the linked photo
 * is usually not on the current page, and may not match the active
 * filters at all.
 */
export function usePhoto(id: string | null) {
  return useQuery<PhotoRecordDto>({
    queryKey: ['photos', 'one', id],
    queryFn: async () => api.get(`photos/${id}`).json<PhotoRecordDto>(),
    enabled: !!id,
  });
}

export function usePhotoFacets() {
  return useQuery<PhotoFacets>({
    queryKey: ['photos', 'facets'],
    queryFn: async () => api.get('photos/facets').json<PhotoFacets>(),
    staleTime: 5 * 60_000,
  });
}

export function useCreatePhoto() {
  const qc = useQueryClient();
  return useMutation<PhotoRecordDto, Error, PhotoRecordCreateInput>({
    mutationFn: async (input) =>
      api.post('photos', { json: input }).json<PhotoRecordDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

export function useUpdatePhoto() {
  const qc = useQueryClient();
  return useMutation<PhotoRecordDto, Error, { id: string; input: Record<string, unknown> }>({
    mutationFn: async ({ id, input }) =>
      api.patch(`photos/${id}`, { json: input }).json<PhotoRecordDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

export function useDeletePhoto() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await api.delete(`photos/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

/**
 * Permanently delete an already-soft-deleted photo. SUPER_ADMIN only;
 * the API returns 403 for everyone else. Polymorphic comments are
 * cleared in the same transaction. R2 storage objects are intentionally
 * left in place (separate cleanup job).
 */
export function useHardDeletePhoto() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await api.delete(`photos/${id}/permanent`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

export function useRestorePhoto() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await api.post(`photos/${id}/restore`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

export function useNearbyParcels(photoId: string | null, search: string = '') {
  const trimmed = search.trim();
  return useQuery<NearbyParcelsResponse>({
    // Search is part of the cache key so React Query holds onto each
    // query string's result independently. staleTime keeps the
    // unfiltered "most appropriate" head warm while the user types.
    queryKey: ['photos', photoId, 'nearby-parcels', trimmed],
    queryFn: async () => {
      const url = trimmed
        ? `photos/${photoId}/nearby-parcels?q=${encodeURIComponent(trimmed)}`
        : `photos/${photoId}/nearby-parcels`;
      return api.get(url).json<NearbyParcelsResponse>();
    },
    enabled: !!photoId,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export function useAssignParcel() {
  const qc = useQueryClient();
  return useMutation<PhotoRecordDto, Error, { photoId: string; parcelId: string }>({
    mutationFn: async ({ photoId, parcelId }) =>
      api
        .patch(`photos/${photoId}`, { json: { parcelId } })
        .json<PhotoRecordDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['photos'] });
    },
  });
}

export function useParcelPhotos(parcelId: string | null) {
  return useQuery<PhotoRecordDto[]>({
    queryKey: ['parcels', parcelId, 'photos'],
    queryFn: async () => {
      const res = await api
        .get(`parcels/${parcelId}/photos`)
        .json<{ items: PhotoRecordDto[] }>();
      return res.items;
    },
    enabled: !!parcelId,
  });
}
